import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Phone, MapPin, Clock, Shield, FileText, Scale } from 'lucide-react';

// Logo de La Petite Épicerie
const LOGO_URL = "https://customer-assets.emergentagent.com/job_loyaltyhub-3/artifacts/bd853roq_La%20petite%20e%CC%81picerie-logo.png";

// Composant Email obfusqué (anti-spam bots)
function ObfuscatedEmail({ className = "" }) {
  const [email, setEmail] = useState("chargement...");
  
  useEffect(() => {
    // Décodage côté client uniquement (invisible pour les bots)
    const parts = ["vmmarket", "la-petite-epicerie", "shop"];
    setEmail(`${parts[0]}@${parts[1]}.${parts[2]}`);
  }, []);
  
  const handleClick = () => {
    const parts = ["vmmarket", "la-petite-epicerie", "shop"];
    window.location.href = `mailto:${parts[0]}@${parts[1]}.${parts[2]}`;
  };
  
  return (
    <button onClick={handleClick} className={`hover:text-white transition-colors ${className}`}>
      {email}
    </button>
  );
}

// Configuration de l'entreprise - INFORMATIONS OFFICIELLES
const COMPANY_INFO = {
  // EXPLOITANT DU SITE (Client)
  name: "La Petite Épicerie",
  legalName: "VM MARKET - SAS (Société par Actions Simplifiée)", 
  address: "138 Rue Arthur Lamendin",
  postalCode: "62980",
  city: "Vermelles",
  country: "France",
  phone: "07 80 64 89 76",
  // Email obfusqué - ne pas mettre en clair ici
  siret: "944 108 638 00015",
  siren: "944 108 638",
  tva: "FR34 944 108 638",
  rcs: "Béthune",
  codeNaf: "4711B - Commerce d'alimentation générale",
  director: "Mohamed ARHAZ",
  hours: "7j/7 de 12h à minuit • 🌙 Ramadan: Ven-Sam jusqu'à 4h",
  
  // ÉDITEUR DU LOGICIEL (Développeur propriétaire)
  developer: {
    name: "Abdelghani Baaloulou",
    status: "Entrepreneur individuel",
    siren: "428 992 796",
    siret: "428 992 796 00022",
    tva: "FR54428992796",
    activity: "6311Z - Traitement de données, hébergement et activités connexes",
    since: "2000",
    phone: "06 52 89 30 09",
  },
  
  // HÉBERGEUR
  host: "Emergent Labs",
  hostAddress: "San Francisco, USA",
};

// Modal pour les mentions légales complètes
function LegalModal({ isOpen, onClose, type }) {
  if (!isOpen) return null;

  const content = {
    mentions: {
      title: "Mentions Légales",
      icon: <FileText className="w-6 h-6" />,
      content: (
        <div className="space-y-6 text-gray-300">
          <section>
            <h3 className="text-lg font-semibold text-white mb-2">1. Exploitant du site</h3>
            <p>Le site la-petite-epicerie.shop est exploité par :</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong>VM MARKET</strong> - SAS (Société par Actions Simplifiée)</li>
              <li>Nom commercial : <strong>{COMPANY_INFO.name}</strong></li>
              <li>Adresse : {COMPANY_INFO.address}, {COMPANY_INFO.postalCode} {COMPANY_INFO.city}</li>
              <li>SIRET : {COMPANY_INFO.siret}</li>
              <li>N° TVA : {COMPANY_INFO.tva}</li>
              <li>Code NAF : {COMPANY_INFO.codeNaf}</li>
              <li>Directeur de publication : {COMPANY_INFO.director}</li>
              <li>Contact : <ObfuscatedEmail /></li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">2. Éditeur du logiciel</h3>
            <p>L'application est développée et éditée par :</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong>{COMPANY_INFO.developer.name}</strong></li>
              <li>Statut : {COMPANY_INFO.developer.status}</li>
              <li>SIRET : {COMPANY_INFO.developer.siret}</li>
              <li>N° TVA : {COMPANY_INFO.developer.tva}</li>
              <li>Activité : {COMPANY_INFO.developer.activity}</li>
              <li>En activité depuis : {COMPANY_INFO.developer.since}</li>
            </ul>
            <p className="mt-3 text-sm text-pink-400">
              © {new Date().getFullYear()} - Tous droits réservés. Le code source, le design et les fonctionnalités 
              de cette application sont la propriété exclusive de {COMPANY_INFO.developer.name}.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">3. Hébergement</h3>
            <p>Le site est hébergé par :</p>
            <ul className="list-disc list-inside mt-2">
              <li>{COMPANY_INFO.host}</li>
              <li>{COMPANY_INFO.hostAddress}</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">4. Propriété intellectuelle</h3>
            <p>
              <strong>Logiciel :</strong> L'application, son code source, son architecture et son design sont la propriété 
              exclusive de <strong>{COMPANY_INFO.developer.name}</strong>. Toute reproduction, copie ou utilisation 
              non autorisée est strictement interdite.
            </p>
            <p className="mt-2">
              <strong>Contenu :</strong> Les contenus spécifiques au commerce (logo, produits, données clients) 
              sont la propriété de VM MARKET.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">5. Module Heartbeats™</h3>
            <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4 mt-2">
              <p className="text-pink-300 font-semibold mb-2">🛡️ Propriété exclusive de {COMPANY_INFO.developer.name}</p>
              <p className="text-sm">
                Le module de rencontres <strong>Heartbeats™</strong> et tous ses composants sont la propriété 
                intellectuelle exclusive de {COMPANY_INFO.developer.name} (SIRET: {COMPANY_INFO.developer.siret}).
              </p>
              <div className="mt-3 text-sm">
                <p className="font-medium text-white">Éléments protégés :</p>
                <ul className="list-disc list-inside mt-1 space-y-1 text-gray-300">
                  <li>Personnages IA : <span className="text-pink-400">Luna™, Enzo™, Pixie™, Nyx™, Kurson™</span></li>
                  <li>Algorithmes : Soul™, Psyche™, Compatibilité</li>
                  <li>Fonctionnalités : Destins Croisés™, Blind Date™, Shadow Mode™</li>
                  <li>Système économique : HBT Tokens™</li>
                </ul>
              </div>
              <p className="mt-3 text-xs text-gray-400">
                VM MARKET (La Petite Épicerie) dispose d'une licence d'utilisation uniquement. 
                La propriété intellectuelle reste à 100% celle de l'auteur.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">6. Données personnelles (RGPD)</h3>
            <p>
              Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, de portabilité 
              et de suppression de vos données. Contact : <ObfuscatedEmail />
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">6. Horaires d'ouverture</h3>
            <p>{COMPANY_INFO.hours}</p>
          </section>
        </div>
      )
    },
    cgv: {
      title: "Conditions Générales de Vente",
      icon: <Scale className="w-6 h-6" />,
      content: (
        <div className="space-y-6 text-gray-300">
          <section>
            <h3 className="text-lg font-semibold text-white mb-2">Article 1 - Objet</h3>
            <p>
              Les présentes CGV régissent les ventes de produits et services proposés par {COMPANY_INFO.name} 
              sur le site la-petite-epicerie.shop et en magasin.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">Article 2 - Programme de fidélité</h3>
            <p>
              Le programme de fidélité permet aux clients de cumuler des tampons pour obtenir des récompenses.
              Les tampons sont valables 12 mois à compter de leur obtention.
              Une carte de fidélité complète donne droit à un produit gratuit selon les conditions en vigueur.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">Article 3 - Livraison</h3>
            <p>
              Les livraisons sont effectuées dans les zones définies. Les frais de livraison sont indiqués 
              avant la validation de la commande. Le délai de livraison estimé est communiqué lors de la commande.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">Article 4 - Jeux et concours</h3>
            <p>
              Les jeux proposés dans l'application sont gratuits et limités à une participation par jour.
              Les gains (points, tampons, réductions) sont crédités automatiquement sur le compte client.
              Les codes promotionnels obtenus sont valables 30 jours.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">Article 5 - Données personnelles</h3>
            <p>
              Les données collectées sont utilisées uniquement pour la gestion du compte client, 
              des commandes et de la communication. Elles ne sont jamais vendues à des tiers.
            </p>
          </section>
        </div>
      )
    },
    privacy: {
      title: "Politique de Confidentialité",
      icon: <Shield className="w-6 h-6" />,
      content: (
        <div className="space-y-6 text-gray-300">
          <section>
            <h3 className="text-lg font-semibold text-white mb-2">Données collectées</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Nom, prénom, téléphone, email</li>
              <li>Adresse de livraison</li>
              <li>Historique des achats et tampons</li>
              <li>Données de navigation (cookies)</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">Utilisation des données</h3>
            <p>Vos données sont utilisées pour :</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Gérer votre compte fidélité</li>
              <li>Traiter vos commandes et livraisons</li>
              <li>Vous envoyer des offres promotionnelles (avec votre accord)</li>
              <li>Améliorer nos services</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">Vos droits (RGPD)</h3>
            <p>Vous pouvez à tout moment :</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Accéder à vos données</li>
              <li>Les rectifier ou les supprimer</li>
              <li>Retirer votre consentement</li>
              <li>Demander la portabilité de vos données</li>
            </ul>
            <p className="mt-2">Contact : <ObfuscatedEmail /></p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-white mb-2">Cookies</h3>
            <p>
              Ce site utilise des cookies pour améliorer votre expérience. 
              Vous pouvez les désactiver dans les paramètres de votre navigateur.
            </p>
          </section>
        </div>
      )
    }
  };

  const currentContent = content[type] || content.mentions;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-x-hidden overflow-y-auto shadow-2xl border border-gray-700"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-black/30">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500">
                {currentContent.icon}
              </div>
              <h2 className="text-xl font-bold text-white">{currentContent.title}</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {currentContent.content}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-700 bg-black/30 text-center">
            <p className="text-sm text-gray-500">
              Dernière mise à jour : Décembre 2024
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Composant Footer principal
export default function Footer({ variant = "full" }) {
  const [modalType, setModalType] = useState(null);
  const currentYear = new Date().getFullYear();

  // Version compacte pour les pages internes
  if (variant === "compact") {
    return (
      <>
        <footer className="py-4 px-6 border-t border-gray-800 bg-black/30">
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500">
            <span>© {currentYear} {COMPANY_INFO.name}</span>
            <span className="hidden sm:inline">•</span>
            <button onClick={() => setModalType('mentions')} className="hover:text-pink-400 transition-colors">
              Mentions légales
            </button>
            <button onClick={() => setModalType('cgv')} className="hover:text-pink-400 transition-colors">
              CGV
            </button>
            <button onClick={() => setModalType('privacy')} className="hover:text-pink-400 transition-colors">
              Confidentialité
            </button>
          </div>
        </footer>
        <LegalModal isOpen={!!modalType} onClose={() => setModalType(null)} type={modalType} />
      </>
    );
  }

  // Version complète pour la landing page
  return (
    <>
      <footer className="py-12 px-6 border-t border-gray-800 bg-gradient-to-b from-transparent to-black/50">
        <div className="max-w-6xl mx-auto">
          {/* Grid principal */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Colonne 1 - À propos */}
            <div>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                <img 
                  src={LOGO_URL} 
                  alt="La Petite Épicerie" 
                  className="h-10 w-auto"
                  style={{ filter: 'drop-shadow(0 0 10px rgba(0, 255, 255, 0.5))' }}
                />
                {COMPANY_INFO.name}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Fidélité récompensée, livraison offerte ! 🚀 Rejoins le programme Client-Malin et profite d'avantages exclusifs chaque semaine.
              </p>
            </div>

            {/* Colonne 2 - Contact */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Contact</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-pink-400" />
                  {COMPANY_INFO.address}, {COMPANY_INFO.postalCode} {COMPANY_INFO.city}
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-pink-400" />
                  <a href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">
                    {COMPANY_INFO.phone}
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-pink-400" />
                  <ObfuscatedEmail />
                </li>
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-pink-400" />
                  {COMPANY_INFO.hours}
                </li>
              </ul>
              
              {/* Réseaux sociaux */}
              <div className="mt-4 pt-4 border-t border-gray-800">
                <h4 className="text-sm font-semibold text-white mb-3">Suivez-nous</h4>
                <div className="flex items-center gap-3">
                  <a 
                    href="https://www.instagram.com/la_petite_epicerie.shop/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-pink-500/30 text-pink-400 text-xs font-medium hover:from-purple-500/30 hover:to-pink-500/30 transition-all"
                  >
                    📸 Instagram
                  </a>
                  <a 
                    href="https://www.facebook.com/profile.php?id=61588543479522"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-400 text-xs font-medium hover:bg-blue-500/30 transition-all"
                  >
                    👤 Facebook
                  </a>
                  <a 
                    href="https://www.snapchat.com/@lapetiteepi62"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 text-xs font-medium hover:bg-yellow-500/30 transition-all"
                  >
                    👻 Snap
                  </a>
                </div>
              </div>
            </div>

            {/* Colonne 3 - Liens légaux */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Informations</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <button 
                    onClick={() => setModalType('mentions')}
                    className="text-gray-400 hover:text-pink-400 transition-colors flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Mentions légales
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setModalType('cgv')}
                    className="text-gray-400 hover:text-pink-400 transition-colors flex items-center gap-2"
                  >
                    <Scale className="w-4 h-4" />
                    Conditions générales de vente
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setModalType('privacy')}
                    className="text-gray-400 hover:text-pink-400 transition-colors flex items-center gap-2"
                  >
                    <Shield className="w-4 h-4" />
                    Politique de confidentialité
                  </button>
                </li>
                {/* 💜 Lien subtil vers Heartbeats */}
                <li className="pt-2 mt-2 border-t border-gray-800">
                  <a 
                    href="https://heartbeats.fr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-pink-400 transition-colors flex items-center gap-2 text-xs"
                    data-testid="footer-heartbeats-link"
                  >
                    💜 Heartbeats - Rencontres locales
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-8 border-t border-gray-800 text-center">
            <p className="text-sm text-gray-500">
              © {currentYear} {COMPANY_INFO.name}. Tous droits réservés.
            </p>
            <p className="text-xs text-gray-600 mt-2">
              Application développée avec <span className="text-pink-500">❤</span> par <span className="text-pink-400">{COMPANY_INFO.developer.name}</span>
            </p>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <LegalModal isOpen={!!modalType} onClose={() => setModalType(null)} type={modalType} />
    </>
  );
}
